import { useEffect, useRef, useState } from "react";
import { Camera, Upload, Send } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import Reveal from "@/components/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useUiTexts } from "@/lib/uiTexts";

function FanPhotoSubmitDialog({ open, onClose }) {
  const inputRef = useRef(null);
  const [pseudo, setPseudo] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/fan-photos/upload", fd);
      setImageUrl(data.url);
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/fan-photos", { pseudo, image_url: imageUrl });
      toast.success("Photo envoyée ! Elle apparaîtra sur le mur après validation par l'équipe.");
      setPseudo("");
      setImageUrl("");
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-orange-500/30 bg-[#0B132B] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold uppercase">Envoyer ma photo</DialogTitle>
          <DialogDescription>
            Portez votre maillot Emisphere et rejoignez le mur des supporters ! Votre photo sera publiée après validation par l'équipe.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <input placeholder="Votre pseudo" value={pseudo} required maxLength={30}
            data-testid="fan-photo-pseudo-input" onChange={(e) => setPseudo(e.target.value)}
            className="w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500" />
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="fan-photo-file-input" />
          {imageUrl ? (
            <img src={imageUrl} alt="Aperçu" className="max-h-56 w-full rounded-lg object-contain" data-testid="fan-photo-preview" />
          ) : (
            <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
              data-testid="fan-photo-upload-button"
              className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-orange-500/40 px-4 py-8 text-sm font-bold uppercase tracking-wider text-orange-400 transition-colors hover:bg-orange-500/10 disabled:opacity-50">
              <Upload className="h-5 w-5" /> {uploading ? "Envoi en cours..." : "Choisir une photo (max 8 Mo)"}
            </button>
          )}
          <button type="submit" disabled={sending || !imageUrl || !pseudo.trim()} data-testid="fan-photo-submit-button"
            className="flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98] disabled:opacity-50">
            <Send className="h-4 w-4" /> {sending ? "Envoi..." : "Envoyer pour validation"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function FanWall() {
  const [photos, setPhotos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const texts = useUiTexts();

  useEffect(() => {
    api.get("/fan-photos").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  return (
    <section className="border-b border-orange-500/20 bg-[#0B132B]/40" data-testid="fan-wall-section">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">{texts.fanwall_label}</p>
              <h2 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">
                {texts.fanwall_title}
              </h2>
              <p className="mt-3 max-w-xl text-sm text-slate-400">
                {texts.fanwall_subtitle}
              </p>
            </div>
            <button onClick={() => setDialogOpen(true)} data-testid="fan-wall-submit-open"
              className="flex items-center gap-2 rounded-xl border border-orange-500/40 px-5 py-3 font-heading text-base font-bold uppercase tracking-wide text-orange-400 transition-all hover:scale-[1.02] hover:bg-orange-500/10">
              <Camera className="h-5 w-5" /> {texts.fanwall_submit}
            </button>
          </div>
        </Reveal>

        {photos.length > 0 ? (
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="fan-wall-grid">
            {photos.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06} y={20}>
                <figure className="group relative overflow-hidden rounded-xl border border-slate-700/60 bg-[#060B18] transition-colors hover:border-orange-500/40"
                  data-testid={`fan-photo-${p.id}`}>
                  <img src={p.image_url} alt={`Photo de ${p.pseudo}`} loading="lazy"
                    className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#060B18] to-transparent px-3 pb-2 pt-8">
                    <span className="font-heading text-sm font-bold uppercase text-orange-400">{p.pseudo}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        ) : (
          <p className="mt-10 rounded-xl border border-dashed border-slate-600 p-8 text-center text-sm text-slate-400" data-testid="fan-wall-empty">
            {texts.fanwall_empty}
          </p>
        )}
      </div>
      <FanPhotoSubmitDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </section>
  );
}
